import { ArrowLeftIcon, DollarSign, ShoppingCart, Star, Users, X, Eye, MousePointerClick, Target, Trophy, LogOut } from 'lucide-react';
import moment from 'moment';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Row } from 'reactstrap';
import { Colxx, Separator } from '../../../../components/common/CustomBootstrap';
import { useDailyAnalytics } from '../../../../components/dashboard/DailyAnalyticsFetcher';
import {
    useFeaturedSpDaily,
    aggregateFeaturedTotals,
    aggregateFeaturedByPlacement,
    hasFeaturedActivity,
    isFeaturedActive,
    listPlacements,
    formatPlacementLabel,
    PLACEMENT_COLORS,
} from '../../../../components/dashboard/FeaturedSpDailyFetcher';
import DashboardCharts from '../../../../components/dashboard/DashboardCharts';
import FeaturedCharts from '../../../../components/dashboard/FeaturedCharts';
import DateRangeSelector from '../../../../components/dashboard/DateRangeSelector';
import StatCard from '../../../../components/dashboard/StatCard';
import { ORDER_COLLECTION } from '../../../../config/env';
import RecentOrders from '../../../../containers/dashboards/RecentOrders';
import TopWashers from '../../../../containers/dashboards/topWashers';
import { firestore } from '../../../../helpers/Firebase';
import { formatNumber } from '../../../../helpers/Utils';

const SelectedProvider = ({ provider, selectProvider }) => {
    const [bookings, setBookings] = useState([]);
    // Date range state - same as main dashboard
    const [dateRangeState, setDateRangeState] = useState(() => {
        const endDate = moment().subtract(1, 'day').endOf('day');
        const startDate = endDate.clone().subtract(6, 'days').startOf('day');
        return {
            startDate: startDate.toDate(),
            endDate: endDate.toDate(),
            startTimestamp: startDate.valueOf(),
            endTimestamp: endDate.valueOf(),
        };
    });

    // Fetch analytics data filtered by provider
    const {
        data: dailyAnalytics,
        previousData: previousDailyAnalytics,
        loading: analyticsLoading,
        error: analyticsError
    } = useDailyAnalytics(dateRangeState, provider?.key);

    // Featured SP performance for this provider only (GA4-sourced).
    const {
        data: featuredDaily,
        previousData: previousFeaturedDaily,
        loading: featuredLoading,
        error: featuredError,
    } = useFeaturedSpDaily(dateRangeState, provider?.key);

    // Show featured cards + charts only when this provider is in an ACTIVE
    // featured window (featured.endDate in the future) OR has historical
    // featured activity in the selected range. Expired featured windows
    // (endDate in the past) are treated as not-currently-featured, matching
    // the mobile app's getFeaturedIndex behavior — so the historical-data
    // fallback is what surfaces past numbers for them.
    const showFeatured = useMemo(() => {
        if (isFeaturedActive(provider?.featured)) return true;
        return hasFeaturedActivity(featuredDaily, provider?.key);
    }, [provider, featuredDaily]);

    // Memoize date ranges for current and previous periods
    const { currentPeriodRange, previousPeriodRange } = useMemo(() => {
        if (!dateRangeState?.startDate || !dateRangeState?.endDate) {
            return { currentPeriodRange: null, previousPeriodRange: null };
        }

        const currentStart = moment(dateRangeState.startDate);
        const currentEnd = moment(dateRangeState.endDate);
        const periodLength = currentEnd.diff(currentStart, 'days');

        const previousStart = currentStart.clone().subtract(periodLength + 1, 'days');
        const previousEnd = currentStart.clone().subtract(1, 'days');

        return {
            currentPeriodRange: `${currentStart.format('MMM DD, YYYY')} - ${currentEnd.format('MMM DD, YYYY')}`,
            previousPeriodRange: `${previousStart.format('MMM DD, YYYY')} - ${previousEnd.format('MMM DD, YYYY')}`
        };
    }, [dateRangeState?.startDate, dateRangeState?.endDate]);


    // Calculate totals from dailyAnalytics for the selected date range with trends
    const totals = useMemo(() => {
        if (!dailyAnalytics || dailyAnalytics.length === 0 || !provider?.key) {
            return [];
        }

        // Aggregate data from all days in the selected range (current period)
        let aggregatedData = {
            totalUsers: 0,
            totalRevenu: 0,
            totalBookings: 0,
            totalRate: { count: 0, rate: 0 },
            counters: {
                onlineBooking: 0,
                offlineBooking: 0,
                canceled: 0,
                rejected: 0
            }
        };

        // Aggregate data from previous period
        let previousAggregatedData = {
            totalUsers: 0,
            totalRevenu: 0,
            totalBookings: 0,
            totalRate: { count: 0, rate: 0 },
            counters: {
                onlineBooking: 0,
                offlineBooking: 0,
                canceled: 0,
                rejected: 0
            }
        };

        // Calculate current period totals
        dailyAnalytics.forEach(day => {
            // Get SP data for this provider
            if (day.SP && day.SP[provider.key]) {
                const spData = day.SP[provider.key];
                aggregatedData.totalBookings += spData.totalBookings || 0;
                aggregatedData.totalRevenu += spData.totalRevenu || 0;
                aggregatedData.totalUsers += spData.totalUsers || 0;

                if (spData.totalRate) {
                    aggregatedData.totalRate.count += spData.totalRate.count || 0;
                    aggregatedData.totalRate.rate += spData.totalRate.rate || 0;
                }

                if (spData.counters) {
                    aggregatedData.counters.onlineBooking += spData.counters.onlineBooking || 0;
                    aggregatedData.counters.offlineBooking += spData.counters.offlineBooking || 0;
                    aggregatedData.counters.canceled += spData.counters.canceled || 0;
                    aggregatedData.counters.rejected += spData.counters.rejected || 0;
                }
            }
        });

        // Calculate previous period totals
        if (previousDailyAnalytics && previousDailyAnalytics.length > 0) {
            previousDailyAnalytics.forEach(day => {
                if (day.SP && day.SP[provider.key]) {
                    const spData = day.SP[provider.key];
                    previousAggregatedData.totalBookings += spData.totalBookings || 0;
                    previousAggregatedData.totalRevenu += spData.totalRevenu || 0;
                    previousAggregatedData.totalUsers += spData.totalUsers || 0;

                    if (spData.totalRate) {
                        previousAggregatedData.totalRate.count += spData.totalRate.count || 0;
                        previousAggregatedData.totalRate.rate += spData.totalRate.rate || 0;
                    }

                    if (spData.counters) {
                        previousAggregatedData.counters.onlineBooking += spData.counters.onlineBooking || 0;
                        previousAggregatedData.counters.offlineBooking += spData.counters.offlineBooking || 0;
                        previousAggregatedData.counters.canceled += spData.counters.canceled || 0;
                        previousAggregatedData.counters.rejected += spData.counters.rejected || 0;
                    }
                }
            });
        }

        // Helper function to calculate trend data object
        const calculateTrendData = (current, previous) => {
            if (current === null || current === undefined) {
                return null;
            }

            const percentage = (!previous || previous === 0)
                ? (current > 0 ? 100 : null)
                : ((current - previous) / previous) * 100;

            return {
                percentage,
                currentTotal: current,
                previousTotal: previous || 0,
                currentPeriod: currentPeriodRange,
                previousPeriod: previousPeriodRange
            };
        };

        // Calculate average rating
        const avgRating = aggregatedData.totalRate.count > 0
            ? (aggregatedData.totalRate.rate / aggregatedData.totalRate.count).toFixed(2)
            : 0;

        const previousAvgRating = previousAggregatedData.totalRate.count > 0
            ? (previousAggregatedData.totalRate.rate / previousAggregatedData.totalRate.count).toFixed(2)
            : 0;

        // Featured SP performance for this provider.
        const featuredCurrent = aggregateFeaturedTotals(featuredDaily, provider?.key);
        const featuredPrevious = aggregateFeaturedTotals(previousFeaturedDaily, provider?.key);

        // Per-placement (af_from) breakdown surfaced via stat-card hover tooltips.
        const featuredCurrentByPlacement = aggregateFeaturedByPlacement(featuredDaily, provider?.key);
        const featuredPreviousByPlacement = aggregateFeaturedByPlacement(previousFeaturedDaily, provider?.key);
        const placementsList = listPlacements(featuredCurrentByPlacement, featuredPreviousByPlacement);
        const buildPlacementItems = (source, field) =>
            placementsList.map((p) => ({
                label: formatPlacementLabel(p),
                value: source[p]?.[field] || 0,
                color: PLACEMENT_COLORS[p] || 'hsl(220, 9%, 60%)',
            }));
        const buildPlacementCtrItems = (source) =>
            placementsList.map((p) => ({
                label: formatPlacementLabel(p),
                value: source[p]?.ctr || 0,
                color: PLACEMENT_COLORS[p] || 'hsl(220, 9%, 60%)',
                formatAsPercent: true,
            }));

        return [
            {
                title: 'Total Users',
                icon: Users,
                value: formatNumber(aggregatedData.totalUsers) || 0,
                color: '#3F51B5',
                trendData: calculateTrendData(aggregatedData.totalUsers, previousAggregatedData.totalUsers)
            },
            {
                title: 'Total Sales',
                icon: DollarSign,
                value: formatNumber(aggregatedData.totalRevenu) || 0,
                color: '#278664',
                formatCurrency: true,
                trendData: calculateTrendData(aggregatedData.totalRevenu, previousAggregatedData.totalRevenu)
            },
            {
                title: 'Average Rating',
                icon: Star,
                value: avgRating || 0,
                color: "#FF9F40",
                trendData: calculateTrendData(parseFloat(avgRating), parseFloat(previousAvgRating))
            },
            {
                title: 'Total Bookings',
                icon: ShoppingCart,
                value: formatNumber(aggregatedData.totalBookings) || 0,
                color: "#9C27B0",
                trendData: calculateTrendData(aggregatedData.totalBookings, previousAggregatedData.totalBookings),
                breakdownData: {
                    currentOnline: aggregatedData.counters.onlineBooking,
                    currentOffline: aggregatedData.counters.offlineBooking,
                    previousOnline: previousAggregatedData.counters.onlineBooking,
                    previousOffline: previousAggregatedData.counters.offlineBooking
                }
            },
            {
                title: 'Total Canceled & Rejected',
                icon: X,
                value: formatNumber(aggregatedData.counters.canceled + aggregatedData.counters.rejected) || 0,
                color: '#dc2626',
                trendData: calculateTrendData(
                    aggregatedData.counters.canceled + aggregatedData.counters.rejected,
                    previousAggregatedData.counters.canceled + previousAggregatedData.counters.rejected
                ),
                invertTrend: true, // Increase is bad
                breakdownData: {
                    currentCanceled: aggregatedData.counters.canceled,
                    currentRejected: aggregatedData.counters.rejected,
                    previousCanceled: previousAggregatedData.counters.canceled,
                    previousRejected: previousAggregatedData.counters.rejected
                }
            },
            ...(showFeatured ? [
                {
                    title: 'Featured Impressions',
                    icon: Eye,
                    value: formatNumber(featuredCurrent.impressions) || 0,
                    color: '#8b5cf6',
                    trendData: calculateTrendData(featuredCurrent.impressions, featuredPrevious.impressions),
                    breakdownData: {
                        currentItems: buildPlacementItems(featuredCurrentByPlacement, 'impressions'),
                        previousItems: buildPlacementItems(featuredPreviousByPlacement, 'impressions'),
                    },
                },
                {
                    title: 'Featured Clicks',
                    icon: MousePointerClick,
                    value: formatNumber(featuredCurrent.clicks) || 0,
                    color: '#ec4899',
                    trendData: calculateTrendData(featuredCurrent.clicks, featuredPrevious.clicks),
                    breakdownData: {
                        currentItems: buildPlacementItems(featuredCurrentByPlacement, 'clicks'),
                        previousItems: buildPlacementItems(featuredPreviousByPlacement, 'clicks'),
                    },
                },
                {
                    title: 'Featured CTR',
                    icon: Target,
                    value: `${featuredCurrent.ctr.toFixed(2)}%`,
                    color: '#f59e0b',
                    trendData: calculateTrendData(featuredCurrent.ctr, featuredPrevious.ctr),
                    breakdownData: {
                        currentItems: buildPlacementCtrItems(featuredCurrentByPlacement),
                        previousItems: buildPlacementCtrItems(featuredPreviousByPlacement),
                    },
                },
                {
                    title: 'Featured Bounce Rate',
                    icon: LogOut,
                    value: `${(featuredCurrent.clicks > 0 ? ((featuredCurrent.clicks - featuredCurrent.orders) / featuredCurrent.clicks) * 100 : 0).toFixed(2)}%`,
                    color: '#ef4444',
                    invertTrend: true,
                    trendData: calculateTrendData(
                        featuredCurrent.clicks > 0 ? ((featuredCurrent.clicks - featuredCurrent.orders) / featuredCurrent.clicks) * 100 : 0,
                        featuredPrevious.clicks > 0 ? ((featuredPrevious.clicks - featuredPrevious.orders) / featuredPrevious.clicks) * 100 : 0,
                    ),
                },
                {
                    title: 'Featured Revenue',
                    icon: Trophy,
                    value: formatNumber(featuredCurrent.revenue) || 0,
                    color: '#14b8a6',
                    formatCurrency: true,
                    trendData: calculateTrendData(featuredCurrent.revenue, featuredPrevious.revenue),
                },
            ] : []),
        ];
    }, [dailyAnalytics, previousDailyAnalytics, featuredDaily, previousFeaturedDaily, showFeatured, provider?.key, currentPeriodRange, previousPeriodRange]);

    // Fetch recent bookings for this provider filtered by date range
    useEffect(() => {
        if (!provider?.key) return;

        firestore().collection(ORDER_COLLECTION)
            .where('sp_id', '==', provider.key)
            .where('created', '>=', dateRangeState.startTimestamp)
            .where('created', '<=', dateRangeState.endTimestamp)
            .orderBy('created', 'desc')
            .limit(6)
            .get()
            .then((snapshot) => {
                const newBookings = [];
                snapshot.docs.forEach(doc => {
                    newBookings.push(doc.data());
                });
                setBookings(newBookings);
            })
            .catch((error) => {
                console.error('Error fetching bookings:', error);
                setBookings([]);
            });
    }, [provider?.key, dateRangeState.startTimestamp, dateRangeState.endTimestamp]);

    const handleDateRangeChange = (newDateRange) => {
        setDateRangeState(newDateRange);
    };

    // Memoize stat cards data
    const statCardsData = useMemo(() => {
        return totals.map((item, index) => {
            const isCurrency = item.formatCurrency ||
                item.title.toLowerCase().includes('sales') ||
                item.title.toLowerCase().includes('revenue');

            return {
                key: `stat_card_${index}`,
                item,
                isCurrency
            };
        });
    }, [totals]);
    return (
        analyticsLoading ? (
            <div className="loading" />
        ) : (
            <Fragment>
                <div className="mb-3">
                    <Row>
                        <Colxx xxs="12" md="6">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <Button
                                    color="primary"
                                    size="sm"
                                    outline
                                    className="back-button"
                                    onClick={() => selectProvider(null)}
                                >
                                    <ArrowLeftIcon size={16} style={{ marginRight: '0.375rem' }} />
                                    Back
                                </Button>
                                <h1 className="mb-2 p-0">{provider.name}</h1></div>
                        </Colxx>
                        <Colxx xxs="12" md="6" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem' }}>
                            <DateRangeSelector
                                onRangeChange={handleDateRangeChange}
                                defaultDays={7}
                                value={dateRangeState}
                            />

                        </Colxx>
                    </Row>
                </div>
                <Separator className="mb-5" />

                {/* Stat Cards */}
                <Row className="mt-2">
                    {statCardsData.map(({ key, item, isCurrency }) => (
                        <Colxx key={key} xxs="12" sm="6" md="6" lg="4" xl="4" className="mb-4">
                            <StatCard
                                title={item.title}
                                value={item.value}
                                icon={item.icon}
                                color={item.color || "#666"}
                                formatCurrency={isCurrency}
                                trendData={item.trendData}
                                invertTrend={item.invertTrend}
                                breakdownData={item.breakdownData}
                            />
                        </Colxx>
                    ))}
                </Row>

                {/* Recent Orders and Top Washers */}
                <Row>
                    {bookings && bookings.length > 0 && (
                        <Colxx xxs="12" md="6" className="mb-4">
                            <RecentOrders bookings={bookings} />
                        </Colxx>
                    )}
                    <Colxx xxs="12" md="6" className="mb-4">
                        <TopWashers
                            dateRange={dateRangeState}
                            dailyAnalytics={dailyAnalytics}
                            previousDailyAnalytics={previousDailyAnalytics}
                            loading={analyticsLoading}
                            error={analyticsError}
                            filterBySpId={provider?.key}
                        />
                    </Colxx>

                    {/* Dashboard Charts - filtered by provider */}
                    <DashboardCharts
                        dateRange={dateRangeState}
                        dailyAnalytics={dailyAnalytics}
                        previousDailyAnalytics={previousDailyAnalytics}
                        loading={analyticsLoading}
                        error={analyticsError}
                        filterBySpId={provider?.key}
                    />

                    {/* Featured Charts - filtered by provider */}
                    {showFeatured && (
                        <FeaturedCharts
                            dateRange={dateRangeState}
                            featuredDaily={featuredDaily}
                            previousFeaturedDaily={previousFeaturedDaily}
                            loading={featuredLoading}
                            error={featuredError}
                            filterBySpId={provider?.key}
                        />
                    )}
                </Row>
            </Fragment>
        )
    );
};

export default SelectedProvider;
